import { AuthProvider, useAuth } from './auth';
import { AppErrorBoundary, AppShell, LoadingScreen, ProtectedRoute } from './components';
import {
  AccountPage,
  AccountPasswordPage,
  AdminLoginPage,
  AuditPage,
  CreateOrganizationPage,
  EmailSettingsPage,
  EmailTestPage,
  ForgotPasswordPage,
  HomePage,
  InvitePage,
  LocationRequestsPage,
  LoginPage,
  OrganizationDashboardPage,
  OrganizationActivityPage,
  OrganizationInvitationsPage,
  OrganizationSecurityPage,
  OrganizationSettingsPage,
  OrganizationSettingsPanelRedirect,
  OrganizationsPage,
  PlatformOrganizationDetailPage,
  PlatformOrganizationActivityPage,
  PlatformOrganizationInvitationsPage,
  PlatformOrganizationLocationsPage,
  PlatformOrganizationSettingsPage,
  PlatformOrganizationSubscriptionPage,
  PlatformOrganizationUsersPage,
  PlatformDashboard,
  PlatformDomainsPage,
  PlatformReleasesPage,
  PlatformUsersPage,
  PlansManagementPage,
  ResetPasswordPage,
  StaffPage,
  SupportAccessPage,
} from './pages';
import { BrowserRouter, Navigate, RouteParamsProvider, matchRoute, useLocation, useParams } from './router';
import { PatientDetailPage, PatientsPage, SchedulePage, ServiceTypesPage } from './clinical-pages';
import { AppointmentDetailPage } from './appointment-ui';
import { ClinicalEvaluationWorkspacePage, ClinicalWorkspacePage, PatientClinicalChartPage } from './clinical-documentation';
import { BillingCenterPage, PatientBillingPage } from './billing-pages';
import { CollectionsCenterPage } from './collections-pages';
import { BillingStatementPublicPage } from './billing-statement-page';
import { InsuranceCenterPage } from './insurance-pages';
import { DataImportPage, ImportMappingReviewPage } from './import-pages';
import { FormsPage } from './forms-pages';
import { ProvidersPage } from './provider-pages';
import { ProviderSettingsPage } from './provider-settings-page';
import { CommunicationsAutomationPage, CommunicationsInboxPage, CommunicationsNotificationsPage, CommunicationsRemindersPage, CommunicationsTemplatesPage, CommunicationsWaitlistPage, CommunicationThreadPage, StaffCollaborationThreadPage } from './communications-pages';
import { SquaredAwayPayPage, SquaredAwayPayPublicPage, SquaredAwayReceiptPage } from './squared-away-pay-pages';
import { PatientPortalActivatePage, PatientPortalForgotPasswordPage, PatientPortalFormPage, PatientPortalLoginPage, PatientPortalOnboardingPage, PatientPortalResetPasswordPage, PatientPortalWorkspacePage } from './patient-portal';
import type { ReactNode } from 'react';
import { NotFoundPage } from './not-found-page';
import { PotentialClientsPage } from './prospect-pages';
import { CommercialBillingSetupPage, PlatformCommercialBillingPage } from './commercial-billing-pages';
import { BillingStaffGuidePage, StaffHelpPage } from './help-pages';
import { ReleaseConsolePage } from './release-console-page';
import { BillingPartnerWorkbenchPage } from './billing-partner-workbench';

type RouteDefinition = {
  pattern: string;
  element: ReactNode;
  public?: boolean;
};

function OrganizationRevenueRedirect({ target }: { target: string }) {
  const { organizationId = '' } = useParams();
  return <Navigate to={`/organizations/${organizationId}${target}`} replace />;
}

const routes: RouteDefinition[] = [
  { pattern: '/admin', element: <AdminLoginPage />, public: true },
  { pattern: '/admin/login', element: <Navigate to="/admin" replace />, public: true },
  { pattern: '/login', element: <LoginPage />, public: true },
  { pattern: '/forgot-password', element: <ForgotPasswordPage />, public: true },
  { pattern: '/reset-password/:token', element: <ResetPasswordPage />, public: true },
  { pattern: '/invite/:token', element: <InvitePage />, public: true },
  { pattern: '/pay/:token', element: <SquaredAwayPayPublicPage />, public: true },
  { pattern: '/receipt/:token', element: <SquaredAwayReceiptPage />, public: true },
  { pattern: '/billing/setup/:token', element: <CommercialBillingSetupPage />, public: true },
  { pattern: '/statement/:token', element: <BillingStatementPublicPage />, public: true },
  { pattern: '/patient/login', element: <PatientPortalLoginPage />, public: true },
  { pattern: '/patient/forgot-password', element: <PatientPortalForgotPasswordPage />, public: true },
  { pattern: '/patient/reset-password/:token', element: <PatientPortalResetPasswordPage />, public: true },
  { pattern: '/patient/activate/:token', element: <PatientPortalActivatePage />, public: true },
  { pattern: '/patient/onboarding', element: <PatientPortalOnboardingPage />, public: true },
  { pattern: '/patient/messages/:threadId', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/patient/messages', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/patient/notifications', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/patient/communication-preferences', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/patient/forms/:assignmentId', element: <PatientPortalFormPage />, public: true },
  { pattern: '/patient/forms', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/patient/billing', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/patient/appointments', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/patient/profile', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/patient/help', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/patient', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/organizations/:organizationId/patient/login', element: <PatientPortalLoginPage />, public: true },
  { pattern: '/organizations/:organizationId/patient/forgot-password', element: <PatientPortalForgotPasswordPage />, public: true },
  { pattern: '/organizations/:organizationId/patient/reset-password/:token', element: <PatientPortalResetPasswordPage />, public: true },
  { pattern: '/organizations/:organizationId/patient/activate/:token', element: <PatientPortalActivatePage />, public: true },
  { pattern: '/organizations/:organizationId/patient/onboarding', element: <PatientPortalOnboardingPage />, public: true },
  { pattern: '/organizations/:organizationId/patient/messages/:threadId', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/organizations/:organizationId/patient/messages', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/organizations/:organizationId/patient/notifications', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/organizations/:organizationId/patient/communication-preferences', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/organizations/:organizationId/patient/forms/:assignmentId', element: <PatientPortalFormPage />, public: true },
  { pattern: '/organizations/:organizationId/patient/forms', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/organizations/:organizationId/patient/billing', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/organizations/:organizationId/patient/appointments', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/organizations/:organizationId/patient/profile', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/organizations/:organizationId/patient/help', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/organizations/:organizationId/patient', element: <PatientPortalWorkspacePage />, public: true },
  { pattern: '/', element: <HomePage /> },
  { pattern: '/account', element: <AccountPage /> },
  { pattern: '/account/password', element: <AccountPasswordPage /> },
  { pattern: '/platform', element: <PlatformDashboard /> },
  { pattern: '/partner/reports', element: <BillingPartnerWorkbenchPage /> },
  { pattern: '/partner/requests', element: <BillingPartnerWorkbenchPage /> },
  { pattern: '/partner/follow-up', element: <BillingPartnerWorkbenchPage /> },
  { pattern: '/partner/payments', element: <BillingPartnerWorkbenchPage /> },
  { pattern: '/partner/claims', element: <BillingPartnerWorkbenchPage /> },
  { pattern: '/partner/coverage', element: <BillingPartnerWorkbenchPage /> },
  { pattern: '/partner/work', element: <BillingPartnerWorkbenchPage /> },
  { pattern: '/partner', element: <BillingPartnerWorkbenchPage /> },
  { pattern: '/platform/organizations/new', element: <CreateOrganizationPage /> },
  { pattern: '/platform/organizations/:organizationId/locations', element: <PlatformOrganizationLocationsPage /> },
  { pattern: '/platform/organizations/:organizationId/users', element: <PlatformOrganizationUsersPage /> },
  { pattern: '/platform/organizations/:organizationId/subscription', element: <PlatformOrganizationSubscriptionPage /> },
  { pattern: '/platform/organizations/:organizationId/invitations', element: <PlatformOrganizationInvitationsPage /> },
  { pattern: '/platform/organizations/:organizationId/activity', element: <PlatformOrganizationActivityPage /> },
  { pattern: '/platform/organizations/:organizationId/settings', element: <PlatformOrganizationSettingsPage /> },
  { pattern: '/platform/organizations/:organizationId', element: <PlatformOrganizationDetailPage /> },
  { pattern: '/platform/organizations', element: <OrganizationsPage /> },
  { pattern: '/platform/potential-clients', element: <PotentialClientsPage /> },
  { pattern: '/platform/commercial-billing', element: <PlatformCommercialBillingPage /> },
  { pattern: '/platform/settings/users', element: <PlatformUsersPage /> },
  { pattern: '/platform/users', element: <Navigate to="/platform/settings/users" replace /> },
  { pattern: '/platform/plans', element: <PlansManagementPage /> },
  { pattern: '/platform/location-requests', element: <LocationRequestsPage /> },
  { pattern: '/platform/audit', element: <AuditPage /> },
  { pattern: '/platform/support-access', element: <SupportAccessPage /> },
  { pattern: '/platform/settings/domains', element: <PlatformDomainsPage /> },
  { pattern: '/platform/settings/releases', element: <PlatformReleasesPage /> },
  { pattern: '/release', element: <ReleaseConsolePage /> },
  { pattern: '/platform/settings/email-test', element: <EmailTestPage /> },
  { pattern: '/platform/settings/email', element: <EmailSettingsPage /> },
  { pattern: '/organizations/:organizationId/appointments/:appointmentId', element: <AppointmentDetailPage /> },
  { pattern: '/organizations/:organizationId/patients/:patientId/clinical', element: <PatientClinicalChartPage /> },
  { pattern: '/organizations/:organizationId/patients/:patientId/billing', element: <PatientBillingPage /> },
  { pattern: '/organizations/:organizationId/patients/:patientId', element: <PatientDetailPage /> },
  { pattern: '/organizations/:organizationId/patients', element: <PatientsPage /> },
  { pattern: '/organizations/:organizationId/schedule', element: <SchedulePage /> },
  { pattern: '/organizations/:organizationId/providers', element: <ProvidersPage /> },
  { pattern: '/organizations/:organizationId/provider-settings', element: <ProviderSettingsPage /> },
  { pattern: '/organizations/:organizationId/service-types', element: <ServiceTypesPage /> },
  { pattern: '/organizations/:organizationId/forms', element: <FormsPage /> },
  { pattern: '/organizations/:organizationId/clinical', element: <ClinicalWorkspacePage /> },
  { pattern: '/organizations/:organizationId/patients/:patientId/clinical/evaluations/:evaluationId', element: <ClinicalEvaluationWorkspacePage /> },
  { pattern: '/organizations/:organizationId/insurance/coverage', element: <InsuranceCenterPage initialTab="coverage" /> },
  { pattern: '/organizations/:organizationId/insurance/eligibility', element: <InsuranceCenterPage initialTab="eligibility" /> },
  { pattern: '/organizations/:organizationId/insurance/prior-auth', element: <InsuranceCenterPage initialTab="prior-auth" /> },
  { pattern: '/organizations/:organizationId/insurance/payers', element: <InsuranceCenterPage initialTab="payers" /> },
  { pattern: '/organizations/:organizationId/insurance', element: <InsuranceCenterPage /> },
  { pattern: '/organizations/:organizationId/collections/balances', element: <CollectionsCenterPage initialTab="balances" /> },
  { pattern: '/organizations/:organizationId/collections/invoices', element: <CollectionsCenterPage initialTab="invoices" /> },
  { pattern: '/organizations/:organizationId/collections/payments', element: <CollectionsCenterPage initialTab="payments" /> },
  { pattern: '/organizations/:organizationId/collections/follow-up', element: <CollectionsCenterPage initialTab="followups" /> },
  { pattern: '/organizations/:organizationId/collections', element: <CollectionsCenterPage /> },
  { pattern: '/organizations/:organizationId/squared-away-pay', element: <SquaredAwayPayPage /> },
  { pattern: '/organizations/:organizationId/billing/review', element: <BillingCenterPage initialWorkspace="review" /> },
  { pattern: '/organizations/:organizationId/billing/configuration/guided', element: <BillingCenterPage initialTab="configuration" initialBillingSetupArea="guided" /> },
  { pattern: '/organizations/:organizationId/billing/configuration/payer-codes', element: <BillingCenterPage initialTab="configuration" initialBillingSetupArea="codes" /> },
  { pattern: '/organizations/:organizationId/billing/configuration/formulas', element: <BillingCenterPage initialTab="configuration" initialBillingSetupArea="formulas" /> },
  { pattern: '/organizations/:organizationId/billing/configuration/payer-rules', element: <BillingCenterPage initialTab="configuration" initialBillingSetupArea="payers" /> },
  { pattern: '/organizations/:organizationId/billing/configuration/partners', element: <BillingCenterPage initialTab="configuration" initialBillingSetupArea="partners" /> },
  { pattern: '/organizations/:organizationId/billing/configuration', element: <BillingCenterPage initialTab="configuration" /> },
  { pattern: '/organizations/:organizationId/billing/charges', element: <BillingCenterPage initialTab="charges" /> },
  { pattern: '/organizations/:organizationId/billing/claims', element: <BillingCenterPage initialTab="claims" /> },
  { pattern: '/organizations/:organizationId/billing/remittance', element: <BillingCenterPage initialTab="remittance" /> },
  { pattern: '/organizations/:organizationId/billing/invoices', element: <OrganizationRevenueRedirect target="/collections/invoices" /> },
  { pattern: '/organizations/:organizationId/billing/statements', element: <OrganizationRevenueRedirect target="/collections/invoices" /> },
  { pattern: '/organizations/:organizationId/billing/collections', element: <OrganizationRevenueRedirect target="/collections" /> },
  { pattern: '/organizations/:organizationId/billing/payments', element: <OrganizationRevenueRedirect target="/collections/payments" /> },
  { pattern: '/organizations/:organizationId/billing/follow-ups', element: <OrganizationRevenueRedirect target="/billing/claims" /> },
  { pattern: '/organizations/:organizationId/billing/reports', element: <BillingCenterPage initialTab="reports" /> },
  { pattern: '/organizations/:organizationId/billing', element: <BillingCenterPage /> },
  { pattern: '/organizations/:organizationId/communications/inbox/:threadId', element: <CommunicationThreadPage /> },
  { pattern: '/organizations/:organizationId/communications/team/:threadId', element: <StaffCollaborationThreadPage /> },
  { pattern: '/organizations/:organizationId/communications/notifications', element: <CommunicationsNotificationsPage /> },
  { pattern: '/organizations/:organizationId/communications/waitlist', element: <CommunicationsWaitlistPage /> },
  { pattern: '/organizations/:organizationId/communications/reminders', element: <CommunicationsRemindersPage /> },
  { pattern: '/organizations/:organizationId/communications/automation', element: <CommunicationsAutomationPage /> },
  { pattern: '/organizations/:organizationId/communications/templates', element: <CommunicationsTemplatesPage /> },
  { pattern: '/organizations/:organizationId/communications', element: <CommunicationsInboxPage /> },
  { pattern: '/organizations/:organizationId/invitations', element: <OrganizationInvitationsPage /> },
  { pattern: '/organizations/:organizationId/activity', element: <OrganizationActivityPage /> },
  { pattern: '/organizations/:organizationId/security', element: <OrganizationSecurityPage /> },
  { pattern: '/organizations/:organizationId/imports/mapping-review', element: <ImportMappingReviewPage /> },
  { pattern: '/organizations/:organizationId/imports', element: <DataImportPage /> },
  { pattern: '/organizations/:organizationId/settings', element: <OrganizationSettingsPage /> },
  { pattern: '/organizations/:organizationId/locations', element: <OrganizationSettingsPanelRedirect panel="locations" /> },
  { pattern: '/organizations/:organizationId/staff', element: <StaffPage /> },
  { pattern: '/organizations/:organizationId/help/billing', element: <BillingStaffGuidePage /> },
  { pattern: '/organizations/:organizationId/help', element: <StaffHelpPage /> },
  { pattern: '/organizations/:organizationId/subscription', element: <OrganizationSettingsPanelRedirect panel="subscription" /> },
  { pattern: '/organizations/:organizationId', element: <OrganizationDashboardPage /> },
];

function RoutedApp() {
  const { pathname } = useLocation();
  const { me } = useAuth();

  for (const route of routes) {
    const params = matchRoute(route.pattern, pathname);
    if (params === null) continue;

    const page = <RouteParamsProvider params={params}>{route.element}</RouteParamsProvider>;
    if (route.public) return page;
    const organizationId = params.organizationId;
    if (me && organizationId && me.user.platformRole === 'NONE') {
      const membership = me.memberships.find((item: any) => item.organization.id === organizationId);
      const roleCodes = new Set(membership?.roles.map((role: any) => role.code) || []);
      const providerOnly = Boolean(membership && roleCodes.has('provider') && !membership.isOrganizationOwner && !roleCodes.has('organization_admin'));
      const billingPartnerOnly = Boolean(membership && (roleCodes.has('billing_partner') || roleCodes.has('billing_partner_specialist')) && !membership.isOrganizationOwner && !roleCodes.has('organization_admin'));
      if (providerOnly) {
        const base = `/organizations/${organizationId}`;
        const allowedProviderPath = pathname === `${base}/schedule`
          || pathname === `${base}/clinical`
          || pathname === `${base}/provider-settings`
          || pathname === `${base}/communications`
          || pathname.startsWith(`${base}/communications/`)
          || pathname === `${base}/help`
          || pathname.startsWith(`${base}/help/`)
          || pathname === `${base}/patients`
          || pathname.startsWith(`${base}/patients/`)
          || pathname.startsWith(`${base}/appointments/`);
        if (pathname === base || !allowedProviderPath) return <Navigate to={`${base}/schedule`} replace />;
      }
      if (billingPartnerOnly) {
        const base = `/organizations/${organizationId}`;
        const allowedBillingPath = pathname === `${base}/schedule`
          || pathname === `${base}/patients`
          || pathname.startsWith(`${base}/patients/`)
          || pathname === `${base}/billing`
          || pathname.startsWith(`${base}/billing/`)
          || pathname === `${base}/insurance`
          || pathname === `${base}/help`
          || pathname.startsWith(`${base}/help/`)
          || pathname.startsWith(`${base}/appointments/`);
        if (pathname === base || !allowedBillingPath) return <Navigate to="/partner" replace />;
      }
    }
    return <ProtectedRoute><AppShell>{page}</AppShell></ProtectedRoute>;
  }

  return <NotFoundRoute />;
}

function NotFoundRoute() {
  const { me, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!me) return <NotFoundPage />;
  return <ProtectedRoute><AppShell><NotFoundPage embedded /></AppShell></ProtectedRoute>;
}

export default function App() {
  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <RoutedApp />
        </AuthProvider>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}
